/**
 * Single source for the OpenAI Ads pixel id on the client.
 *
 * A module rather than `process.env.NEXT_PUBLIC_OPENAI_ADS_PIXEL_ID` read at each call site: Next
 * inlines `NEXT_PUBLIC_*` at build time, so the value is a literal in the bundle and cannot be changed
 * — or removed — at runtime. Behind one export, the "no pixel configured" path stays reachable in
 * tests (`jest.mock`) and there is one place to look when the id has to move accounts.
 *
 * Public by nature: it ships in the HTML of every page. The conversions key is its opposite and never
 * leaves the server — see `src/pages/api/openai-ads-conversion.ts`.
 */
import EnvironmentsEnum from '@/enums/environments-enum';

export const OPENAI_ADS_PIXEL_ID = process.env.NEXT_PUBLIC_OPENAI_ADS_PIXEL_ID ?? '';

const SUPPRESSED_ENVIRONMENTS: string[] = [EnvironmentsEnum.Staging, EnvironmentsEnum.Development];

/**
 * True only in local/staging — never in production, and never when `NEXT_PUBLIC_ENV` is unset.
 *
 * Production and staging share one pixel id, so without this gate a QA signup on staging lands in the
 * real ad account as a conversion: it inflates the count the optimizer learns from, and there is no way
 * to tell it apart afterwards — the event carries no environment of its own. Separate pixels per
 * environment would be the other fix; this one needs no new ad account.
 *
 * The direction is deliberate, and matches `shouldUseMockBlog` in `wp-fetch.ts`: an unknown or missing
 * value falls through to reporting. Silently losing production conversions is worse than a stray
 * staging one, and staging is the environment we control the env of.
 */
export const isOpenAiAdsSuppressed = (): boolean => SUPPRESSED_ENVIRONMENTS.includes(process.env.NEXT_PUBLIC_ENV ?? '');
